import { KatalComponent, nextUniqueId } from '../../shared/base';

const BOUND_TEXTAREA_EVENT_NAMES = [
  'blur',
  'change',
  'input',
  'focus',
  'keydown',
  'keyup',
  'mouseup',
  'click',
];

const SUPPRESSED_TEXTAREA_EVENT_NAMES = [
  'blur',
  'change',
  'input',
  'keydown',
  'keyup',
  'mouseup',
  'click',
];

/**
 * @component {kat-textarea} KatalTextarea For large blocks of text, larger than an input can reasonably hold
 * @status Production
 * @theme flo
 * @example Empty {"value": "", "placeholder": "", "disabled": "", "label": "", "constraint-label": "", "constraint-emphasis": "", "state": "", "state-emphasis": "", "state-label": ""}
 * @example Placeholder {"value": "", "placeholder": "Place example text here", "disabled": "", "label": "", "constraint-label": "", "constraint-emphasis": "", "state": "", "state-emphasis": "", "state-label": ""}
 * @example Pre-filled {"value": "Lorem ipsum...", "placeholder": "", "disabled": "", "label": "", "constraint-label": "", "constraint-emphasis": "", "state": "", "state-emphasis": "", "state-label": ""}
 * @example Disabled {"value": "Lorem ipsum...", "placeholder": "", "disabled": "true", "label": "", "constraint-label": "", "constraint-emphasis": "", "state": "", "state-emphasis": "", "state-label": ""}
 * @example Complex {"value": "Way back when I was just a little bitty boy living in a box under the stairs of corner of the basement of the house half a block down the street from Jerrys Bait Shop; you know the place...", "disabled": "", "placeholder": "Way back when I was...", "label": "Life Story", "constraint-label": "Must be thorough and dramatic", "constraint-emphasis": "Note", "state": "error", "state-emphasis": "Warning", "state-label": "Stories about New Mexico are too long and not permitted"}
 * @example WithTooltip {"label": "With Tooltip", "tooltip-text": "A tooltip with some additional information", "tooltip-position": "right"}
 * @event blur This event is fired whenever the textarea is un-selected
 * @event input This event is fired whenever the user types a single character
 * @event change This event is fired whenever the user commits their change
 * @event focus This event is fired whenever the textarea is selected
 * @event keydown This event is fired whenever the keyboard is starts entering a character
 * @event keyup This event is fired whenever the keyboard is finished entering a character
 * @event mouseup This event is fired whenever the mouse button is released
 */
export class KatalTextarea extends KatalComponent {
  static get observedAttributes() {
    return [
      'value',
      'label',
      'unique-id',
      'placeholder',
      'constraint-emphasis',
      'constraint-label',
      'state',
      'state-emphasis',
      'state-label',
      'disabled',
      'name',
      'required',
      'maxlength',
      'tooltip-text',
      'tooltip-position',
      'tooltip-trigger-icon',
    ];
  }

  constructor() {
    super(KatalTextarea.observedAttributes);
  }

  connectedCallback() {
    super.connectedCallback();
    this.render();

    this.registerEvents();
  }

  get uniqueId() {
    let id = this.getAttribute('unique-id');

    if (!id || !id.length) {
      id = nextUniqueId();
      this.uniqueId = id;
    }

    return id;
  }

  set uniqueId(value) {
    this.removeOrSetAttribute('unique-id', value);
  }

  /**
   * @param {string} value This is the text that will be displayed to the user
   */
  get value() {
    return this.getAttribute('value') || '';
  }

  set value(value) {
    this.removeOrSetAttribute('value', value);
  }

  /**
   * @param {string} name This is the name that will be submitted in a form
   */
  get name() {
    return this.getAttribute('name') || '';
  }

  set name(value) {
    this.removeOrSetAttribute('name', value);
  }

  /**
   * @param {string} placeholder This is the text that will be displayed to the user until the user enters text
   */
  get placeholder() {
    return this.getAttribute('placeholder') || '';
  }

  set placeholder(placeholder) {
    this.removeOrSetAttribute('placeholder', placeholder);
  }

  /**
   * @param {string} label Label that describes what the input represents
   */
  get label() {
    return this.getAttribute('label') || '';
  }

  set label(value) {
    this.removeOrSetAttribute('label', value);
  }

  /**
   * @param {string} constraint-emphasis Optional pretext for the constraint label.  Used to provide additional context to the constraint for the input
   */
  get constraintEmphasis() {
    return this.getAttribute('constraint-emphasis') || '';
  }

  set constraintEmphasis(value) {
    this.removeOrSetAttribute('constraint-emphasis', value);
  }

  /**
   * @param {string} constraint-label Provides users with more information about what they enter into the input
   */
  get constraintLabel() {
    return this.getAttribute('constraint-label') || '';
  }

  set constraintLabel(value) {
    this.removeOrSetAttribute('constraint-label', value);
  }

  /**
   * @param {enum} state Setting the state of the input changes its look to give the user more information about what they have entered.  This value must be set for State Labels to show up.
   * @enum {value} error Lets the user know there is a problem with the value they have entered in the input
   */
  get state() {
    return this.getAttribute('state') || '';
  }

  set state(value) {
    this.removeOrSetAttribute('state', value);
  }

  /**
   * @param {string} state-emphasis Optional pretext for the state label.  Used to provide addtional context to the state for the input
   */
  get stateEmphasis() {
    return this.getAttribute('state-emphasis') || '';
  }

  set stateEmphasis(value) {
    this.removeOrSetAttribute('state-emphasis', value);
  }

  /**
   * @param {string} state-label Provides users with more information about why the input is in the state its in
   */
  get stateLabel() {
    return this.getAttribute('state-label') || '';
  }

  set stateLabel(value) {
    this.removeOrSetAttribute('state-label', value);
  }

  /**
   * @param {boolean} disabled prevents the user from changing the value of an input and prevents events from being triggered
   */
  get disabled() {
    return this.getBooleanAttribute('disabled');
  }

  set disabled(value) {
    this.setBooleanAttribute('disabled', value);
  }

  /**
   * @param {boolean} required prevents the user from submitting unless provided
   */
  get required() {
    return this.getBooleanAttribute('required');
  }

  set required(value) {
    this.setBooleanAttribute('required', value);
  }

  /**
   * @param {string} maxlength The maximum number of characters allowed in the element
   */
  get maxlength() {
    return this.getAttribute('maxlength') || '';
  }

  set maxlength(value) {
    this.removeOrSetAttribute('maxlength', value);
  }

  // Note: For the spacing attribute we just need the JSON metadata not the actual getter and setter.
  /**
   * @param {boolean} katal-1-2-spacing-fixes A special compatibility flag that when set removes extra margins around this component and sets its height to
   * the correct default. Note that this will become the default behavior in the next major release of Katal. Take a look at our service bulletin page for
   * more details.
   */
  get katal12SpacingFixes() {
    return this.getBooleanAttribute('katal-1-2-spacing-fixes');
  }

  set katal12SpacingFixes(value) {
    return this.setBooleanAttribute('katal-1-2-spacing-fixes', value);
  }

  /**
   * @classprop {string} tooltip-text This is the text to be shown inside the tooltip placed next to the label text. The tooltip will only appear if this is
   * set.
   */
  get tooltipText() {
    return this.getAttributeOrDefault('tooltip-text', '');
  }

  set tooltipText(value) {
    this.removeOrSetAttribute('tooltip-text', value);
  }

  /**
   * @classprop {string} tooltip-position This is the position of the tooltip. Defaults to "top".
   */
  get tooltipPosition() {
    return this.getAttributeOrDefault('tooltip-position', 'top');
  }

  set tooltipPosition(value) {
    this.removeOrSetAttribute('tooltip-position', value);
  }

  /**
   * @classprop {string} tooltip-trigger-icon This is the icon that triggers the tooltip next to the label. Defaults to "help_outline".
   */
  get tooltipTriggerIcon() {
    return this.getAttributeOrDefault('tooltip-trigger-icon', 'help_outline');
  }

  set tooltipTriggerIcon(value) {
    this.removeOrSetAttribute('tooltip-trigger-icon', value);
  }

  render() {
    const uniqueId = this.uniqueId;
    const placeholder = this.placeholder;
    const disabled = this.disabled;
    const required = this.required;
    const label = this.label;
    const constraintLabel = this.constraintLabel;
    const constraintEmphasis = this.constraintEmphasis;
    const state = this.state;
    const stateEmphasis = this.stateEmphasis;
    const stateLabel = this.stateLabel;
    const name = this.name;
    const maxlength = this.maxlength;

    const querySelectorOrCreateElement = (selector, elementTag) => {
      let element = this.querySelector(selector);

      if (!element) {
        element = document.createElement(elementTag);
        this.appendChild(element);
      }

      return element;
    };

    const formLabelElement = querySelectorOrCreateElement(
      `kat-label.form-label`,
      'kat-label'
    );
    formLabelElement.setAttribute('class', 'form-label'); // only used to identify this label, that it already exists
    formLabelElement.setAttribute('for', uniqueId);
    formLabelElement.setAttribute('text', label);
    formLabelElement.tooltipText = this.tooltipText;
    formLabelElement.tooltipPosition = this.tooltipPosition;
    formLabelElement.tooltipTriggerIcon = this.tooltipTriggerIcon;
    if (label.length) {
      formLabelElement.setAttribute('style', '');
    } else {
      formLabelElement.setAttribute('style', 'display:none;');
    }

    const textareaElement = querySelectorOrCreateElement(
      'textarea',
      'textarea'
    );
    textareaElement.setAttribute('id', uniqueId);
    textareaElement.setAttribute('class', state);
    textareaElement.setAttribute('placeholder', placeholder);
    textareaElement.setAttribute('name', name);
    // Avoid setting value so the cursor position doesn't get reset (like in IE 11.0)
    if (textareaElement.value !== this.value) {
      textareaElement.value = this.value;
    }

    if (disabled) {
      textareaElement.setAttribute('disabled', '');
    } else {
      textareaElement.removeAttribute('disabled');
    }

    if (required) {
      textareaElement.setAttribute('required', '');
    } else {
      textareaElement.removeAttribute('required');
    }

    if (maxlength) {
      textareaElement.setAttribute('maxlength', maxlength);
    } else {
      textareaElement.removeAttribute('maxlength');
    }

    const constraintElement = querySelectorOrCreateElement(
      `kat-label.constraint-label`,
      'kat-label'
    );
    constraintElement.setAttribute('class', 'constraint-label'); // only used to identify this label, that it already exists
    constraintElement.setAttribute('for', uniqueId);
    constraintElement.setAttribute('variant', 'constraint');
    constraintElement.setAttribute('state', 'default');
    constraintElement.setAttribute('emphasis', constraintEmphasis);
    constraintElement.setAttribute('text', constraintLabel);
    if (constraintEmphasis.length || constraintLabel.length) {
      constraintElement.setAttribute('style', '');
    } else {
      constraintElement.setAttribute('style', 'display:none;');
    }

    const stateElement = querySelectorOrCreateElement(
      `kat-label.state-label`,
      'kat-label'
    );
    stateElement.setAttribute('class', 'state-label'); // only used to identify this label, that it already exists
    stateElement.setAttribute('for', uniqueId);
    stateElement.setAttribute('variant', 'constraint');
    stateElement.setAttribute('text', stateLabel);
    stateElement.setAttribute('emphasis', stateEmphasis);
    stateElement.setAttribute('state', state);

    if (stateLabel.length || stateEmphasis.length) {
      stateElement.setAttribute('style', '');
    } else {
      stateElement.setAttribute('style', 'display:none;');
    }
  }

  registerEvents() {
    const input = this.querySelector('textarea');

    BOUND_TEXTAREA_EVENT_NAMES.forEach(name => {
      input.addEventListener(name, e => {
        this.handleEvent(e);
      });
    });
  }

  handleEvent(event) {
    // Only prevent propagation of events we intend to capture. For example, Firefox depends on input's focus event to draw the cursor
    if (SUPPRESSED_TEXTAREA_EVENT_NAMES.includes(event.type)) {
      event.stopImmediatePropagation();
    }

    // Bubble up all bound events except change and exit here
    if (event.type !== 'change') {
      this.dispatchCustomEvent(event.type, { value: event.target.value });
      return;
    }

    // For change events, only update top level component if the input actually differs from this component's "value" attribute
    const bubblingInputValue = event.target.value;
    const currentComponentValue = this.value;

    if (bubblingInputValue !== currentComponentValue) {
      this.dispatchCustomEvent(event.type, { value: event.target.value });
      this.setAttribute('value', event.target.value);
    }
  }

  // Respond to attribute changes.
  attributeChangedCallback() {
    this.render();
    this.dispatchCustomEvent('changed', { value: this.value }, false);
  }
}

KatalComponent.register('kat-textarea', KatalTextarea);
